import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import get from 'lodash/get';
import { DataGrid } from '@mui/x-data-grid';
import { nanoid } from '@reduxjs/toolkit';
import PriceToPolishWords from 'price-to-polish-words';

import { useGetInvoice } from 'api';
import { downloadPdf, createPdfExportObject } from 'engine/documentExporter';
import navigationPaths from 'engine/navigationPaths';

import * as Styled from 'components/ui/documentPreview/DocumentPreview.styles';

import { doDeleteInvoiceAsync } from 'features/invoices/invoicesSlice';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import ContractorSummaryPanel from 'components/contractor/contractorSummaryPanel';
import DashboardLayout from 'components/dashboardLayout/dashboardLayout';
import BillzTypography from 'components/BillzTypographies/BillzTypography';
import BillzTypographySubTitle from 'components/BillzTypographies/BillzTypographySubTitle';
import BillzTypographyTitle from 'components/BillzTypographies/BillzTypographyTitle';
import BillzLabeledTypograpthy from 'components/BillzTypographies/BillzLabeledTypograpthy';
import DocumentStatus from 'components/documentStatus';
import { Fieldset } from 'components/ui/documentPreview/Fieldset/Fieldset';
import { VerticalFieldset } from 'components/ui/documentPreview/VerticalFieldset/VerticalFieldset';
import { ContractorBox } from 'components/ui/documentPreview/ContractorBox/ContractorBox';
import { DataGridCell } from 'components/ui/documentPreview/DataGridCell/DataGridCell';
import { TaxSummaryFooter } from 'components/ui/documentPreview/TaxSummaryFooter/TaxSummaryFooter';
import { DataGridHeader } from 'components/ui/documentPreview/DataGridHeader/DataGridHeader';
import { SummaryFieldSet } from 'components/ui/documentPreview/SummaryFieldSet/SummaryFieldSet';
import { PeriodicInvoiceDescriptor } from 'components/ui/documentPreview/PeriodicInvoiceDescriptor/PeriodicInvoiceDescriptor';
import { InvoiceEmailSendDescriptor } from 'components/ui/documentPreview/InvoiceEmailSendDescriptor/InvoiceEmailSendDescriptor';
import { PreviewActionsMenu } from 'components/ui/documentPreview/PreviewActionsMenu/PreviewActionsMenu';
import { FormatWithPrecision } from 'utility/numberFormatting';
import { getLabelForPaymentMethod } from 'utility/paymentMethods';
import { toShortDate } from 'utility/datetimeFormatting';

import { resources } from 'oems/resources';
import { DeleteListItemConfirmationModal } from 'components/DeleteListItemConfirmationModal/DeleteListItemConfirmationModal';
import { useUpdateInvoiceLeftToPayAmount } from 'api/endpoints/postInvoice';
import ToastMessage from 'components/toastMessage';
import { numberToWords } from 'utility/numberToWords';

export const InvoicePreview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const id = get(location, 'state.id', '');
  const [toastData, setToastData] = useState(null);

  const { data: documentData, refetch: refetchDocumentData, isLoading: isLoadingDocumentData } = useGetInvoice(id);

  const { mutate: updateInvoiceLeftToPayAmount, isLoading: isUpdatingInvoiceLeftToPayAmount } =
    useUpdateInvoiceLeftToPayAmount({
      onSuccess: () => {
        refetchDocumentData();
      },
      onError: () => {
        setToastData({ severity: 'error', message: 'Nie udało się zmienić statusu płatności' });
      },
    });

  function getRowId() {
    return nanoid();
  }

  const renderColHeader = (params) => <DataGridHeader value={params.colDef.headerName} />;

  const isDocumentPaidFor = () => {
    return leftToPay === 0;
  };

  const handleEditClick = () => {
    navigate(navigationPaths().InvoicesForm, { state: { mode: 'edit', id: id } });
  };

  const handleExportToPdfClick = async (template) => {
    var payload = createPdfExportObject(template);
    payload.documentId = id;
    await downloadPdf(payload);
  };

  const handleRepeatDocumentClick = () => {
    navigate(navigationPaths().InvoicesForm, { state: { mode: 'editPreview', id: id } });
  };

  const handleMarkAsPaidClick = () => {
    updateInvoiceLeftToPayAmount({ id: id, leftToPay: 0 });
  };

  const handleMarkAsUnpaidClick = () => {
    updateInvoiceLeftToPayAmount({ id: id, leftToPay: amountToPay });
  };

  const handleCorrectionInvoiceClick = () => {
    navigate(navigationPaths().CorrectionInvoicesForm, { state: { mode: 'create', id: id, correction: true } });
  };

  // #region Invoice deleting

  const [deleteDialogConfirmationOpen, setDeleteDialogConfirmationOpen] = React.useState(false);
  const handleDeleteInvoiceClick = (id) => {
    setDeleteDialogConfirmationOpen(true);
  };

  const handleDeleteInvoiceAgreed = () => {
    dispatch(doDeleteInvoiceAsync(id));
    setDeleteDialogConfirmationOpen(false);
    navigate(navigationPaths().Invoices);
  };
  const handleDeleteInvoiceDisagreed = () => {
    setDeleteDialogConfirmationOpen(false);
  };

  // #endregion

  const handleMenuOptionClick = (optionId) => {
    switch (optionId) {
      case 'edit':
        handleEditClick();
        break;
      case 'pdf':
        handleExportToPdfClick('invoice');
        break;
      case 'duplicatePdf':
        handleExportToPdfClick('invoiceDuplicate');
        break;        
      case 'reinvoice':
        handleRepeatDocumentClick();
        break;
      case 'payment':
        if (isDocumentPaidFor()) {
          handleMarkAsUnpaidClick();
        } else {
          handleMarkAsPaidClick();
        }
        refetchDocumentData();
        break;
      case 'correction':
        handleCorrectionInvoiceClick();
        break;
      case 'delete':
        handleDeleteInvoiceClick();
        break;
      default:
        break;
    }
  };

  const itemsColumns = [
    {
      field: 'index',
      headerName: 'L.p.',
      sortable: false,
      width: 15,
      renderHeader: renderColHeader,
      headerAlign: 'center',
      renderCell: (params) => <DataGridCell value={params.row.no} />,
    },
    {
      field: 'name',
      headerName: 'Nazwa',
      flex: 1,
      sortable: false,
      width: 200,
      renderHeader: renderColHeader,
      headerAlign: 'center',
      renderCell: (params) => <DataGridCell value={params.row.name} />,
    },
    {
      field: 'quantity',
      headerName: 'Ilość',
      flex: 0.2,
      minWidth: 70,
      maxWidth: 100,
      sortable: false,
      renderHeader: renderColHeader,
      align: 'right',
      headerAlign: 'center',
      renderCell: (params) => <DataGridCell value={params.row.quantity} />,
    },
    {
      field: 'unit',
      headerName: 'j.m.',
      sortable: false,
      width: 50,
      renderHeader: renderColHeader,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => <DataGridCell value={params.row.unit} />,
    },
    {
      field: 'netPrice',
      headerName: 'Cena netto',
      flex: 1,
      sortable: false,
      width: 120,
      renderHeader: renderColHeader,
      align: 'right',
      headerAlign: 'center',
      renderCell: (params) => <DataGridCell value={FormatWithPrecision(params.row.unitNetPrice)} />,
    },
    {
      field: 'vat',
      headerName: 'VAT(%)',
      sortable: false,
      width: 70,
      renderHeader: renderColHeader,
      align: 'right',
      headerAlign: 'center',
      renderCell: (params) => <DataGridCell value={params.row.vat.name} />,
    },
    {
      field: 'netValue',
      headerName: 'Wartość netto',
      flex: 1,
      sortable: false,
      width: 120,
      renderHeader: renderColHeader,
      align: 'right',
      headerAlign: 'center',
      renderCell: (params) => <DataGridCell value={FormatWithPrecision(params.row.amountNet)} />,
    },
    {
      field: 'grossValue',
      headerName: 'Wartość brutto',
      flex: 1,
      sortable: false,
      width: 120,
      renderHeader: renderColHeader,
      align: 'right',
      headerAlign: 'center',
      renderCell: (params) => <DataGridCell value={FormatWithPrecision(params.row.amountGross)} />,
    },
  ];

  const seller = useMemo(() => { return get(documentData, 'invoice.seller', {}); }, [documentData]);
  const buyer = useMemo(() => { return get(documentData, 'invoice.buyer', {}); }, [documentData]);
  const placeOfIssue = useMemo(() => { return get(documentData, 'invoice.placeOfIssue', ''); }, [documentData]);
  const dateOfIssue = useMemo(() => { return get(documentData, 'invoice.dateOfIssueUtc', ''); }, [documentData]);
  const dateOfSale = useMemo(() => { return get(documentData, 'invoice.dateOfSaleUtc', ''); }, [documentData]);
  const number = useMemo(() => { return get(documentData, 'invoice.number', ''); }, [documentData]);
  const series = useMemo(() => { return get(documentData, 'invoice.series', ''); }, [documentData]);
  const vat = useMemo(() => { return get(documentData, 'invoice.vat', []); }, [documentData]);
  const items = useMemo(() => { return get(documentData, 'invoice.items', []); }, [documentData]);
  const amountPaid = useMemo(() => { return get(documentData, 'invoice.amountPaid', ''); }, [documentData]);
  const amountToPay = useMemo(() => { return get(documentData, 'invoice.amountToPay', ''); }, [documentData]);
  const currency = useMemo(() => { return get(documentData, 'invoice.currency.code', ''); }, [documentData]);
  const amountToPayWords = useMemo(() => { return numberToWords(amountToPay) + ' ' + currency; }, [amountToPay]);
  const leftToPay = useMemo(() => { return get(documentData, 'invoice.leftToPay', ''); }, [documentData]);
  const comments = useMemo(() => { return get(documentData, 'invoice.comments', ''); }, [documentData]);
  const dueDate = useMemo(() => { return get(documentData, 'invoice.payment.dueDateUtc', ''); }, [documentData]);
  const paymentMethod = useMemo(() => { return get(documentData, 'invoice.payment.code', ''); }, [documentData]);
  const settings = useMemo(() => { return get(documentData, 'invoice.settings', {}); }, [documentData]);
  const splitPayment = useMemo(() => { return get(documentData, 'invoice.splitPayment', false); }, [documentData]);

  return (
    <DashboardLayout isLoading={isLoadingDocumentData}>
      {toastData && (
        <ToastMessage
          open
          onClose={() => setToastData(null)}
          severity={toastData.severity}
          message={toastData.message}
        />
      )}
      <DashboardNavbar />
      {deleteDialogConfirmationOpen && (
        <DeleteListItemConfirmationModal
          id="delete-confirmation-dialog"
          onConfirm={handleDeleteInvoiceAgreed}
          onClose={handleDeleteInvoiceDisagreed}
        />
      )}
      <Styled.Root preloading>
        <Styled.Card>
          <Styled.Wrapper>
            <Styled.HeaderRowSpaceBetween>
              <Styled.Logo src={resources.logoDarkImage} />
              <Styled.VerticalLeftAligned>
                <BillzLabeledTypograpthy text={placeOfIssue} label="Miejsce wystawienia" />
                <BillzLabeledTypograpthy text={toShortDate(dateOfIssue)} label="Data wystawienia" />
                <BillzLabeledTypograpthy text={toShortDate(dateOfSale)} label="Data sprzedaży" />
              </Styled.VerticalLeftAligned>
            </Styled.HeaderRowSpaceBetween>
            <Styled.ContractorsRowSpaceBetween>
              <ContractorBox title="Sprzedawca">
                <ContractorSummaryPanel data={seller} />
              </ContractorBox>
              <ContractorBox title="Nabywca">
                <ContractorSummaryPanel data={buyer} />
              </ContractorBox>
            </Styled.ContractorsRowSpaceBetween>
            <Styled.TitleRow>
              <BillzTypographyTitle>
                Faktura VAT {number}/{series}
              </BillzTypographyTitle>
            </Styled.TitleRow>
            <Styled.Divider />
            <Styled.RowSpaceBetween>
              <DataGrid
                sx={{ color: 'text.main', border: 0, '& .MuiDataGrid-iconSeparator': { display: 'none' } }}
                autoHeight={true}
                getRowId={getRowId}
                rows={items}
                rowHeight={32}
                getRowHeight={() => 'auto'}
                headerHeight={36}
                hideFooterPagination={true}
                hideFooter={false}
                columns={itemsColumns}
                disableSelectionOnClick
                disableColumnFilter
                disableColumnMenu
                experimentalFeatures={{ newEditingApi: true }}
                components={{ Footer: TaxSummaryFooter }}
                componentsProps={{ footer: { vat } }}
              />
            </Styled.RowSpaceBetween>
            <Styled.Divider />
            <Styled.FooterVerticalSpaceBetween>
              <VerticalFieldset title="Uwagi:" subtitle={comments} />
              <SummaryFieldSet
                total={FormatWithPrecision(amountToPay)}
                paid={FormatWithPrecision(amountPaid)}
                leftToPay={FormatWithPrecision(leftToPay)}
              />
            </Styled.FooterVerticalSpaceBetween>
            <Styled.FooterVerticalSpaceBetween>
              <Styled.VerticalLeftAligned>
                <Fieldset
                  title="Sposób płatności:"
                  subtitle={getLabelForPaymentMethod(paymentMethod)}
                  whiteSpace="nowrap"
                />
                <Fieldset title="Termin płatności:" subtitle={toShortDate(dueDate)} whiteSpace="nowrap" />
                {splitPayment === true && <BillzTypographySubTitle>Mechanizm podzielonej płatności</BillzTypographySubTitle>}
              </Styled.VerticalLeftAligned>
              <Styled.VerticalLeftAligned>
                <BillzTypographySubTitle>Do zapłaty:</BillzTypographySubTitle>
                <Fieldset title="Słownie:" subtitle={amountToPayWords} />
              </Styled.VerticalLeftAligned>
            </Styled.FooterVerticalSpaceBetween>
          </Styled.Wrapper>
        </Styled.Card>
        <Styled.SideCard>
          <Styled.VerticalLeftAligned>
            <BillzTypographyTitle>
              Faktura {number}/{series}
            </BillzTypographyTitle>
            <PeriodicInvoiceDescriptor
              isPeriodicInvoice={settings.isPeriodicInvoice}
              issuingPeriod={settings.issuingPeriod}
            />
            {leftToPay === 0 ? <DocumentStatus paid /> : <DocumentStatus />}
            <InvoiceEmailSendDescriptor sendEmail={settings.sendEmail} emailToSend={settings.emailToSend} />
          </Styled.VerticalLeftAligned>
          <Styled.Divider />
          <Styled.VerticalLeftAligned sx={{ gap: 1 }}>
            <BillzTypographySubTitle>Akcje</BillzTypographySubTitle>
            <PreviewActionsMenu
              id="invoice-actions"
              edit
              pdf
              duplicatePdf
              reinvoice
              payment
              correction
              remove
              isPaid={leftToPay === 0}
              handleMenuOptionClick={handleMenuOptionClick}
            />
          </Styled.VerticalLeftAligned>
        </Styled.SideCard>
      </Styled.Root>
    </DashboardLayout>
  );
};
